// MBA Lab — Team Time-Zone Overlap Planner UI.
//
// Three-layer split, and the layering is the whole design:
//   lib/timezone-model.js  pure arithmetic on a 48-slot half-hour UTC grid.
//                          No Intl, no DOM, no clock. Fully unit-tested.
//   lib/timezone-zones.js  the ONLY module that touches Intl.
//   this file              DOM, storage, analytics, and the one place that
//                          decides *which instants* to resolve offsets at.
//
// Offsets are resolved at now and at now + 7 days on every recompute and are
// NEVER persisted - only IANA zone ids are. A cached offset is wrong the
// moment a member's region changes its clocks, and the difference between
// those two resolutions is exactly what drives the visible shift warning.
import { store, download, trackToolAction } from './lib/core.js';
import { hydrate, mountSyncIndicator, syncedStore } from './lib/account-sync.js';
import { initToolDock } from './lib/tool-dock.js';
import {
  SCHEMA_VERSION,
  LIMITS,
  MAX_MEMBERS,
  SLOT_MINUTES,
  SLOTS_PER_DAY,
  WEEKDAYS,
  DEFAULT_WORK_START_MIN,
  DEFAULT_WORK_END_MIN,
  createDefaultState,
  sanitizeState,
  parseSavedState,
  serializeForSave,
  addMember,
  removeMember,
  updateMember,
  computePlan,
  analyticsParams,
  dockReading,
  buildMarkdown,
  detectOffsetChanges,
  formatClock,
  formatDuration,
} from './lib/timezone-model.js';
import {
  listZones,
  zoneChoices,
  zoneLabel,
  localZone,
  parseZoneInput,
  withOffsets,
} from './lib/timezone-zones.js';

const TOOL_NAME = 'team-time-zone-planner';
const AUTOSAVE_DEBOUNCE_MS = 400;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function byId(id) {
  return document.getElementById(id);
}

function clearChildren(el) {
  while (el && el.firstChild) el.removeChild(el.firstChild);
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function pct(value) {
  return `${Number(value).toFixed(4)}%`;
}

class TimeZonePlanner {
  constructor() {
    this.form = byId('tz-form');
    // Signed in: also syncs to the account (see lib/account-sync.js). Signed out, or with
    // the feature off, this is a pure passthrough to the same localStorage wrapper.
    this.planStore = syncedStore(TOOL_NAME, store(TOOL_NAME), {
      schemaVersion: SCHEMA_VERSION,
      onStatus: mountSyncIndicator(),
    });
    this.zones = listZones();
    this.zoneSet = new Set(this.zones);
    this.offsetCache = new Map();
    this.rowRefs = new Map();
    this.rafId = null;
    this.saveTimer = null;
    this.hasEngaged = false;
    this.lastResultSignature = null;
    this.startOverConfirming = false;
    this.startOverTimer = null;

    // One pair of instants for the life of the page. Re-resolving against a
    // moving "now" on every keystroke would let the warning flicker on and
    // off as the clock crosses a transition mid-session.
    this.refNow = Date.now();
    this.refLater = this.refNow + WEEK_MS;

    this.cacheElements();
    this.renderZoneDatalist();
    this.renderStaticOptions();

    this.dock = initToolDock({
      dock: byId('tz-dock'),
      jumpTarget: byId('tz-results'),
      returnTarget: byId('tz-form'),
    });

    this.state = this.loadInitialState();
    this.renderMembers();
    this.renderControls();
    this.recompute();
    this.wireEvents();
    trackToolAction(TOOL_NAME, 'open');
  }

  cacheElements() {
    this.els = {
      membersList: byId('tz-members-list'),
      newName: byId('tz-new-name'),
      newZone: byId('tz-new-zone'),
      addBtn: byId('tz-add-btn'),
      useMyZone: byId('tz-use-my-zone'),
      limitNote: byId('tz-member-limit-note'),
      emptyNote: byId('tz-empty-note'),
      zoneList: byId('tz-zone-list'),
      durationGroup: byId('tz-duration-group'),
      deadlineWeekday: byId('tz-deadline-weekday'),
      deadlineTime: byId('tz-deadline-time'),
      deadlineAnchor: byId('tz-deadline-anchor'),
      dstWarning: byId('tz-dst-warning'),
      chart: byId('tz-chart'),
      chartFallback: byId('tz-chart-fallback'),
      overlap: byId('tz-overlap'),
      slots: byId('tz-slots'),
      rotation: byId('tz-rotation'),
      deadlineRows: byId('tz-deadline-rows'),
      download: byId('tz-download'),
      copy: byId('tz-copy'),
      startOver: byId('tz-start-over'),
      feedback: byId('tz-action-feedback'),
    };
  }

  /* --- setup ---------------------------------------------------------- */

  renderZoneDatalist() {
    const list = this.els.zoneList;
    if (!list) return;
    clearChildren(list);
    const fragment = document.createDocumentFragment();
    for (const choice of zoneChoices()) {
      const option = document.createElement('option');
      option.value = choice.label;
      fragment.appendChild(option);
    }
    list.appendChild(fragment);
  }

  renderStaticOptions() {
    const weekday = this.els.deadlineWeekday;
    if (weekday && !weekday.options.length) {
      WEEKDAYS.forEach((label, index) => {
        const option = document.createElement('option');
        option.value = String(index);
        option.textContent = label;
        weekday.appendChild(option);
      });
    }
    const time = this.els.deadlineTime;
    if (time && !time.options.length) {
      for (const option of this.halfHourOptions()) time.appendChild(option);
    }
  }

  halfHourOptions() {
    const options = [];
    for (let slot = 0; slot < SLOTS_PER_DAY; slot++) {
      const minutes = slot * SLOT_MINUTES;
      const option = document.createElement('option');
      option.value = String(minutes);
      option.textContent = formatClock(minutes);
      options.push(option);
    }
    return options;
  }

  loadInitialState() {
    const saved = this.planStore.load();
    if (!saved) return createDefaultState();
    const result = parseSavedState(saved);
    return result.status === 'ok' ? result.state : createDefaultState();
  }

  wireEvents() {
    this.els.addBtn.addEventListener('click', () => this.handleAddMember());
    this.els.newName.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleAddMember();
      }
    });
    if (this.els.useMyZone) {
      this.els.useMyZone.addEventListener('click', () => this.handleUseMyZone());
    }

    this.els.durationGroup.addEventListener('change', (e) => {
      const input = e.target.closest('input[name="tz-duration"]');
      if (!input) return;
      this.state = sanitizeState({ ...this.state, durationMinutes: Number(input.value) });
      this.markEngaged();
      this.scheduleUpdate();
    });

    const deadlineChanged = () => {
      this.state = sanitizeState({
        ...this.state,
        deadline: {
          weekday: Number(this.els.deadlineWeekday.value),
          minutesOfDay: Number(this.els.deadlineTime.value),
          anchorMemberId: this.els.deadlineAnchor.value || null,
        },
      });
      this.markEngaged();
      this.scheduleUpdate();
    };
    this.els.deadlineWeekday.addEventListener('change', deadlineChanged);
    this.els.deadlineTime.addEventListener('change', deadlineChanged);
    this.els.deadlineAnchor.addEventListener('change', deadlineChanged);

    this.els.download.addEventListener('click', () => this.handleDownload());
    this.els.copy.addEventListener('click', () => this.handleCopy());
    this.els.startOver.addEventListener('click', () => this.handleStartOver());
  }

  /* --- offsets -------------------------------------------------------- */

  offsetsAt(refInstantMs) {
    const key = String(refInstantMs);
    let cache = this.offsetCache.get(key);
    if (!cache) {
      cache = new Map();
      this.offsetCache.set(key, cache);
    }
    return this.state.members.map((m) => {
      if (!cache.has(m.zone)) {
        cache.set(m.zone, withOffsets([m], refInstantMs)[0].offsetMinutes);
      }
      return { ...m, offsetMinutes: cache.get(m.zone) };
    });
  }

  /* --- member rows ---------------------------------------------------- */

  handleAddMember() {
    const name = this.els.newName.value.trim();
    const zone = parseZoneInput(this.els.newZone.value, this.zoneSet);
    if (!name && !zone) return;
    if (this.state.members.length >= MAX_MEMBERS) {
      this.els.limitNote.hidden = false;
      return;
    }
    this.state = addMember(this.state, {
      name,
      zone,
      workStartMin: DEFAULT_WORK_START_MIN,
      workEndMin: DEFAULT_WORK_END_MIN,
    });
    this.els.newName.value = '';
    this.els.newZone.value = '';
    this.els.newName.focus();
    this.els.limitNote.hidden = this.state.members.length < MAX_MEMBERS;
    this.renderMembers();
    this.renderControls();
    this.markEngaged();
    this.scheduleUpdate();
  }

  handleUseMyZone() {
    const zone = localZone();
    if (!zone) {
      this.showFeedback('This browser will not share its time zone - pick one from the list.');
      return;
    }
    this.els.newZone.value = zoneLabel(zone);
    this.els.newZone.focus();
  }

  handleRemoveMember(memberId) {
    this.state = removeMember(this.state, memberId);
    this.els.limitNote.hidden = true;
    this.renderMembers();
    this.renderControls();
    this.scheduleUpdate();
  }

  /**
   * Targeted, in-place updates only - never a full renderMembers(). Rebuilding
   * the list would tear down and recreate the input the user is typing into,
   * stealing focus mid-keystroke (the same trap team-charter.js documents).
   */
  handleMemberField(memberId, patch) {
    this.state = updateMember(this.state, memberId, patch);
    this.markEngaged();
    this.scheduleUpdate();
  }

  renderMembers() {
    const list = this.els.membersList;
    clearChildren(list);
    this.rowRefs.clear();
    this.els.emptyNote.hidden = this.state.members.length > 0;

    for (const member of this.state.members) {
      list.appendChild(this.buildMemberRow(member));
    }
  }

  buildMemberRow(member) {
    const row = el('div', 'tool-tz-member');
    const nameId = `tz-name-${member.id}`;
    const zoneId = `tz-zone-${member.id}`;
    const startId = `tz-start-${member.id}`;
    const endId = `tz-end-${member.id}`;

    const nameField = el('div', 'tool-tz-member-field');
    const nameLabel = el('label', null, 'Name');
    nameLabel.setAttribute('for', nameId);
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'form-control';
    nameInput.id = nameId;
    nameInput.maxLength = LIMITS.memberName;
    nameInput.value = member.name;
    nameInput.addEventListener('input', () => this.handleMemberField(member.id, { name: nameInput.value }));
    nameField.append(nameLabel, nameInput);

    const zoneField = el('div', 'tool-tz-member-field');
    const zoneLabelEl = el('label', null, 'Time zone');
    zoneLabelEl.setAttribute('for', zoneId);
    const zoneInput = document.createElement('input');
    zoneInput.type = 'text';
    zoneInput.className = 'form-control';
    zoneInput.id = zoneId;
    zoneInput.setAttribute('list', 'tz-zone-list');
    zoneInput.autocomplete = 'off';
    zoneInput.maxLength = LIMITS.zone;
    zoneInput.value = member.zone ? zoneLabel(member.zone) : '';
    zoneInput.addEventListener('change', () => {
      this.handleMemberField(member.id, { zone: parseZoneInput(zoneInput.value, this.zoneSet) });
    });
    zoneField.append(zoneLabelEl, zoneInput);

    const hoursField = el('div', 'tool-tz-member-hours');
    const startLabel = el('label', null, 'Works from');
    startLabel.setAttribute('for', startId);
    const startSelect = this.buildHourSelect(startId, member.workStartMin, (value) =>
      this.handleMemberField(member.id, { workStartMin: value }));
    const endLabel = el('label', null, 'to');
    endLabel.setAttribute('for', endId);
    const endSelect = this.buildHourSelect(endId, member.workEndMin, (value) =>
      this.handleMemberField(member.id, { workEndMin: value }));
    hoursField.append(startLabel, startSelect, endLabel, endSelect);

    const status = el('p', 'tool-tz-member-status');
    status.hidden = true;
    status.setAttribute('role', 'status');

    const removeBtn = el('button', 'tool-tz-member-remove', 'Remove');
    removeBtn.type = 'button';
    removeBtn.dataset.memberId = member.id;
    removeBtn.setAttribute('aria-label', `Remove ${member.name || 'this member'}`);
    removeBtn.addEventListener('click', () => this.handleRemoveMember(member.id));

    row.append(nameField, zoneField, hoursField, status, removeBtn);
    this.rowRefs.set(member.id, { row, status, removeBtn, zoneInput });
    return row;
  }

  buildHourSelect(id, value, onChange) {
    const select = document.createElement('select');
    select.className = 'form-select';
    select.id = id;
    for (const option of this.halfHourOptions()) select.appendChild(option);
    select.value = String(value);
    select.addEventListener('change', () => onChange(Number(select.value)));
    return select;
  }

  renderControls() {
    for (const input of this.els.durationGroup.querySelectorAll('input[name="tz-duration"]')) {
      input.checked = Number(input.value) === this.state.durationMinutes;
    }
    this.els.deadlineWeekday.value = String(this.state.deadline.weekday);
    this.els.deadlineTime.value = String(this.state.deadline.minutesOfDay);

    const anchor = this.els.deadlineAnchor;
    const current = this.state.deadline.anchorMemberId || '';
    clearChildren(anchor);
    if (!this.state.members.length) {
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Add a member first';
      anchor.appendChild(placeholder);
    }
    for (const member of this.state.members) {
      const option = document.createElement('option');
      option.value = member.id;
      option.textContent = member.name || '(unnamed)';
      anchor.appendChild(option);
    }
    anchor.value = current;
  }

  /* --- recompute ------------------------------------------------------ */

  scheduleUpdate() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.recompute();
      this.scheduleAutosave();
    });
  }

  recompute() {
    const nowMembers = this.offsetsAt(this.refNow);
    const laterMembers = this.offsetsAt(this.refLater);
    const computed = computePlan(this.state, nowMembers);
    const shift = detectOffsetChanges(nowMembers, laterMembers);

    this.lastComputed = computed;
    this.renderMemberStatuses(computed);
    this.renderShiftWarning(shift);
    this.renderChart(computed);
    this.renderOverlap(computed);
    this.renderSlots(computed);
    this.renderRotation(computed);
    this.renderDeadline(computed);

    this.dock?.setReading(dockReading(computed));
    this.dock?.setComplete(computed.overlap.hasOverlap);
    this.maybeTrackResult(computed);
  }

  renderMemberStatuses(computed) {
    for (const segment of computed.segments) {
      const refs = this.rowRefs.get(segment.id);
      if (!refs) continue;
      refs.row.classList.toggle('is-unresolved', !segment.resolved);
      if (segment.resolved) {
        refs.status.hidden = true;
        refs.status.textContent = '';
        refs.zoneInput.removeAttribute('aria-invalid');
      } else {
        refs.status.hidden = false;
        refs.status.textContent = segment.zone
          ? `"${segment.zone}" is not a time zone this browser knows. Pick one from the list - this member is left out of the maths until you do.`
          : 'Pick a time zone. This member is left out of the maths until you do.';
        refs.zoneInput.setAttribute('aria-invalid', 'true');
      }
      refs.removeBtn.setAttribute('aria-label', `Remove ${segment.name || 'this member'}`);
    }
  }

  renderShiftWarning(shift) {
    const box = this.els.dstWarning;
    clearChildren(box);
    box.hidden = !shift.changed;
    if (!shift.changed) return;

    box.appendChild(el('p', 'tool-tz-warning-title', 'A clock change lands inside the next seven days.'));
    const list = el('ul', 'tool-tz-warning-list');
    for (const change of shift.changes) {
      const direction = change.deltaMinutes > 0 ? 'forward' : 'back';
      list.appendChild(el('li', null,
        `${change.name || 'A member'} moves ${direction} by ${formatDuration(Math.abs(change.deltaMinutes))} within the week. Re-run this plan after the change.`));
    }
    box.appendChild(list);
  }

  /* --- visualisation -------------------------------------------------- */

  renderChart(computed) {
    const chart = this.els.chart;
    clearChildren(chart);
    const overlapSpans = computed.overlap.spans || [];

    for (const segment of computed.segments) {
      const row = el('div', 'tool-tz-row');

      const head = el('div', 'tool-tz-row-head');
      head.appendChild(el('span', 'tool-tz-row-name', segment.name || '(unnamed)'));
      head.appendChild(el('span', 'tool-tz-row-meta', segment.resolved
        ? `${segment.workLabel} local · ${segment.utcWorkLabel}`
        : 'time zone not recognised'));
      row.appendChild(head);

      if (!segment.resolved) {
        row.appendChild(el('p', 'tool-tz-row-unresolved', 'Left out until the time zone is picked again.'));
        chart.appendChild(row);
        continue;
      }

      const bar = el('div', 'tool-tz-bar');
      bar.setAttribute('role', 'img');
      bar.setAttribute('aria-label', this.barLabel(segment, computed));
      bar.dataset.tzBar = segment.id;

      for (const span of segment.stretchSpans) bar.appendChild(this.buildSpan('stretch', span));
      for (const span of segment.workSpans) bar.appendChild(this.buildSpan('work', span));
      for (const span of overlapSpans) bar.appendChild(this.buildSpan('overlap', span));

      row.appendChild(bar);
      chart.appendChild(row);
    }

    this.renderChartFallback(computed);
  }

  buildSpan(kind, span) {
    const node = el('span', `tool-tz-seg tool-tz-seg--${kind}`);
    node.style.left = pct(span.startPct);
    node.style.width = pct(span.widthPct);
    return node;
  }

  barLabel(segment, computed) {
    const parts = [
      `${segment.name || 'Unnamed member'}, ${segment.zone || 'no time zone'}.`,
      `Working ${segment.workLabel} local, ${segment.utcWorkLabel}.`,
    ];
    if (computed.overlap.hasOverlap) {
      const window = computed.overlap.windows[0];
      const mine = window.perMember.find((p) => p.id === segment.id);
      parts.push(mine
        ? `Shared window ${mine.label} in their local time.`
        : 'Shared window shown on the same bar.');
    } else {
      parts.push('No window is shared by the whole team.');
    }
    return parts.join(' ');
  }

  /** Same information as the bars, in words - not a consolation prize. */
  renderChartFallback(computed) {
    const list = this.els.chartFallback;
    clearChildren(list);
    for (const segment of computed.segments) {
      if (!segment.resolved) {
        list.appendChild(el('li', null,
          `${segment.name || '(unnamed)'}: time zone not recognised, excluded from the plan.`));
        continue;
      }
      const shared = computed.overlap.hasOverlap
        ? (computed.overlap.windows[0].perMember.find((p) => p.id === segment.id) || null)
        : null;
      const tail = shared
        ? ` Shared window ${shared.label} their time.`
        : ' No shared window.';
      list.appendChild(el('li', null,
        `${segment.name || '(unnamed)'} (${segment.zone || 'no time zone'}): works ${segment.workLabel} local, ${segment.utcWorkLabel}.${tail}`));
    }
  }

  renderOverlap(computed) {
    const box = this.els.overlap;
    clearChildren(box);
    const overlap = computed.overlap;

    if (overlap.memberCount === 0) {
      box.appendChild(el('p', 'tool-tz-verdict', 'Add at least one teammate with a time zone to see a shared window.'));
      return;
    }

    box.appendChild(el('p', 'tool-tz-spread',
      `Your team spans ${formatDuration(overlap.spreadMinutes)} of clock. Overlap is set by your two extreme members, not by how many of you there are.`));

    if (!overlap.hasOverlap) {
      box.appendChild(el('p', 'tool-tz-verdict tool-tz-verdict--none',
        'There is no hour where everyone is inside their own working hours. Stop scheduling your way out of this and run the team on handoffs.'));
      return;
    }

    box.appendChild(el('p', 'tool-tz-verdict',
      `Shared window: ${formatDuration(overlap.longestWindowMinutes)}.`));

    const list = el('ul', 'tool-tz-window-list');
    for (const window of overlap.windows) {
      const item = el('li');
      item.appendChild(el('span', 'tool-tz-window-utc',
        `${formatClock(window.startUtcMin)}-${formatClock(window.endUtcMin)} UTC · ${formatDuration(window.durationMinutes)}`));
      const sub = el('ul', 'tool-tz-window-members');
      for (const person of window.perMember) {
        // person.label already carries its own day marker(s) - see overlapWindows in
        // timezone-model.js, which needs a marker per endpoint, not one for the whole range.
        sub.appendChild(el('li', null, `${person.name || '(unnamed)'}: ${person.label}`));
      }
      item.appendChild(sub);
      list.appendChild(item);
    }
    box.appendChild(list);
  }

  renderSlots(computed) {
    const list = this.els.slots;
    clearChildren(list);
    const top = computed.slots.slice(0, 5);
    if (!top.length) {
      list.appendChild(el('li', 'tool-tz-empty', 'Add teammates to rank meeting slots.'));
      return;
    }
    for (const slot of top) {
      const item = el('li', 'tool-tz-slot');
      item.appendChild(el('span', 'tool-tz-slot-time',
        `${formatClock(slot.startUtcMin)}-${formatClock(slot.endUtcMin)} UTC`));
      item.appendChild(el('span', 'tool-tz-slot-cost', slot.painMinutes === 0
        ? 'Inside everyone\'s working hours'
        : `${formatDuration(slot.painMinutes)} outside somebody's working hours`));
      const who = el('ul', 'tool-tz-slot-who');
      if (!slot.eaters.length) {
        who.appendChild(el('li', null, 'Nobody pays for this one.'));
      } else {
        for (const eater of slot.eaters) {
          const mine = slot.perMember.find((p) => p.id === eater.id);
          who.appendChild(el('li', null,
            `${eater.name || '(unnamed)'} takes it at ${mine ? mine.label : ''} — ${formatDuration(eater.painMinutes)} outside their hours`));
        }
      }
      item.appendChild(who);
      list.appendChild(item);
    }
  }

  renderRotation(computed) {
    const box = this.els.rotation;
    clearChildren(box);
    const plan = computed.rotation;

    if (plan.kind === 'no-members') {
      box.appendChild(el('li', 'tool-tz-empty', 'Add teammates to build a rotation.'));
      return;
    }

    if (plan.kind === 'no-pain') {
      box.appendChild(el('li', 'tool-tz-rotation-note',
        'Every shared slot is inside everyone\'s working hours. Publish one recurring time and stop negotiating.'));
      return;
    }

    if (plan.alternative) {
      box.appendChild(el('li', 'tool-tz-rotation-note', plan.alternative.headline));
      for (const step of plan.alternative.steps) {
        box.appendChild(el('li', 'tool-tz-rotation-alt', step));
      }
      return;
    }

    for (const round of plan.rounds) {
      const item = el('li', 'tool-tz-rotation-round');
      item.appendChild(el('span', 'tool-tz-rotation-week', `Week ${round.round}`));
      item.appendChild(el('span', 'tool-tz-rotation-time', `${formatClock(round.startUtcMin)} UTC`));
      item.appendChild(el('span', 'tool-tz-rotation-who', round.eaters.length
        ? round.eaters.map((e) => `${e.name || '(unnamed)'} pays ${formatDuration(e.painMinutes)}`).join('; ')
        : 'nobody pays'));
      box.appendChild(item);
    }

    const totals = el('li', 'tool-tz-rotation-total');
    totals.textContent = `Over that cycle: ${plan.cumulative
      .map((c) => `${c.name || '(unnamed)'} ${formatDuration(c.painMinutes)}`)
      .join(' · ')}`;
    box.appendChild(totals);
  }

  renderDeadline(computed) {
    const list = this.els.deadlineRows;
    clearChildren(list);
    const deadline = computed.deadline;
    if (!deadline.rows.length) {
      list.appendChild(el('li', 'tool-tz-empty', 'Add teammates to translate a deadline.'));
      return;
    }
    for (const row of deadline.rows) {
      const item = el('li', 'tool-tz-deadline-row');
      if (row.isAnchor) item.classList.add('is-anchor');
      item.appendChild(el('span', 'tool-tz-deadline-name', row.name || '(unnamed)'));
      item.appendChild(el('span', 'tool-tz-deadline-when', `${row.weekdayLabel} ${row.timeLabel}`));
      if (row.dayShiftLabel) {
        item.appendChild(el('span', 'tool-tz-deadline-shift', row.dayShiftLabel));
      }
      list.appendChild(item);
    }
  }

  /* --- persistence, analytics, exports -------------------------------- */

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
  }

  /**
   * Coarse buckets and counts only - never a zone id, never a member name.
   * Sent once per distinct result rather than on every keystroke.
   */
  maybeTrackResult(computed) {
    if (computed.overlap.memberCount === 0) return;
    const params = analyticsParams(computed);
    const signature = `${params.member_count}|${params.has_overlap}|${params.spread_hours_bucket}`;
    if (signature === this.lastResultSignature) return;
    this.lastResultSignature = signature;
    // result_v2, not result: tool_result is at GA4's param-count ceiling - see analytics-events.js.
    trackToolAction(TOOL_NAME, 'result_v2', params);
  }

  scheduleAutosave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.autosave(), AUTOSAVE_DEBOUNCE_MS);
  }

  autosave() {
    if (!serializeForSave(this.state)) return;
    this.planStore.save(this.state);
  }

  handleDownload() {
    download('team-time-zone-plan.md', 'text/markdown', buildMarkdown(this.state, this.lastComputed));
    this.showFeedback('Downloaded.');
    trackToolAction(TOOL_NAME, 'download');
  }

  async handleCopy() {
    const markdown = buildMarkdown(this.state, this.lastComputed);
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(markdown);
        copied = true;
      }
    } catch {
      copied = false;
    }
    if (!copied) copied = this.fallbackCopy(markdown);
    this.showFeedback(copied ? 'Copied!' : 'Could not copy - try downloading instead.');
    if (copied) trackToolAction(TOOL_NAME, 'copy');
  }

  fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  }

  handleStartOver() {
    if (!this.startOverConfirming) {
      this.startOverConfirming = true;
      this.els.startOver.textContent = 'Click again to confirm';
      this.startOverTimer = setTimeout(() => {
        this.startOverConfirming = false;
        this.els.startOver.textContent = 'Start over';
      }, 4000);
      return;
    }
    clearTimeout(this.startOverTimer);
    this.startOverConfirming = false;
    this.els.startOver.textContent = 'Start over';
    this.planStore.deleteAccountState();
    this.state = createDefaultState();
    this.lastResultSignature = null;
    this.els.limitNote.hidden = true;
    this.renderMembers();
    this.renderControls();
    this.recompute();
  }

  showFeedback(message) {
    this.els.feedback.textContent = message;
    setTimeout(() => {
      if (this.els.feedback.textContent === message) this.els.feedback.textContent = '';
    }, 3000);
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  if (!byId('tz-form')) return;
  // Reconcile with the account BEFORE the tool reads its own storage, so it simply loads
  // whatever won. A no-op (and no request) when signed out, and bounded by its own short
  // timeout so a slow network delays startup rather than blocking it.
  await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION);
  new TimeZonePlanner();
});
